import React, { Component, Suspense } from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import * as pcoSongsActions from './actions/pco_songs_actions';
import SubNav from '../sub_nav/sub_nav';
import LoadingIndicator from '../../global/loading_indicator/loading_indicator';
import ThemeHelpBox from '../themes/theme_help_box';
import Button from '../../global/button/button';

const SectionHeader = React.lazy(() => import('../../global/table_elements/section_header/section_header'));
const TableRow = React.lazy(() => import('../../global/table_elements/table_row/table_row'));

class PcoSongsLayout extends Component {

    constructor(props) {
        super(props);
        this.state = { searchQuery: '', selectedArrangementIndex: 0, activeTab: 'lyrics' };
        this.searchTimeout = null;
    }

    componentDidMount() {
        this.props.onFetchAuthStatus();
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.connected && this.props.connected) {
            this.props.onFetchSongs();
            this.loadSongFromUrl();
        }
        if (prevProps.loading_auth_status && !this.props.loading_auth_status && this.props.connected) {
            this.props.onFetchSongs();
            this.loadSongFromUrl();
        }
    }

    loadSongFromUrl() {
        const path = this.props.location.pathname;
        const match = path.match(/\/slides\/pco_songs\/(\d+)/);
        if (match && match[1]) {
            this.props.onFetchSongDetail(match[1]);
        }
    }

    goToPage = (pageLink) => {
        this.props.history.push(pageLink);
    }

    handleSearchChange = (e) => {
        const query = e.target.value;
        this.setState({ searchQuery: query });

        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.props.onFetchSongs(0, query);
        }, 300);
    }

    selectSong = (songId) => {
        this.setState({ selectedArrangementIndex: 0, activeTab: 'lyrics' });
        this.props.onFetchSongDetail(songId);
        this.goToPage('/slides/pco_songs/' + songId);
    }

    formatLyrics(lyrics) {
        if (!lyrics) return null;
        return lyrics.split('\n').map((line, i) => {
            if (line.trim() === '') return <br key={i} />;
            return <p key={i} className="leading-relaxed">{line}</p>;
        });
    }

    renderConnectPrompt() {
        return (
            <div>
                <SubNav />
                <div className="max-w-lg mx-auto py-16 px-4 text-center">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Connect to Planning Center</h2>
                    <p className="text-gray-500 mb-8">
                        Sign in to your Planning Center account to browse and view your song library.
                    </p>
                    <a
                        href={this.props.connect_url}
                        className="inline-block bg-blue-500 text-white font-semibold px-6 py-3 rounded-lg cursor-pointer hover:bg-blue-600"
                    >
                        Connect to Planning Center
                    </a>
                </div>
            </div>
        );
    }

    renderSongDetail() {
        if (this.props.loading_song_detail) {
            return (
                <div className="flex items-center justify-center h-full">
                    <LoadingIndicator />
                </div>
            );
        }

        const song = this.props.selected_song;
        if (!song) {
            return (
                <div className="flex items-center justify-center h-full text-gray-400">
                    Select a song to view details
                </div>
            );
        }

        const arrangements = song.arrangements || [];
        const arrangement = arrangements.length > 0 ? arrangements[this.state.selectedArrangementIndex] : null;
        const { activeTab } = this.state;

        const tabClass = (tab) =>
            "py-2 px-4 text-sm font-medium border-b-2 cursor-pointer " +
            (activeTab === tab
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300");

        return (
            <div className="py-4">
                <div className="flex mb-4" style={{ gap: 8 }}>
                    <button
                        onClick={() => this.props.onExportSong(song.id, this.state.selectedArrangementIndex, 'pro', song.title)}
                        className="text-sm font-medium text-blue-500 border border-blue-500 rounded-sm px-3 py-1 cursor-pointer hover:bg-blue-500 hover:text-white"
                    >
                        Export .pro
                    </button>
                    <button
                        onClick={() => this.props.onExportSong(song.id, this.state.selectedArrangementIndex, 'probundle', song.title)}
                        className="text-sm font-medium text-blue-500 border border-blue-500 rounded-sm px-3 py-1 cursor-pointer hover:bg-blue-500 hover:text-white"
                    >
                        Export .proBundle
                    </button>
                    <a
                        href={"https://services.planningcenteronline.com/songs/" + song.id}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-gray-500 border border-gray-400 rounded-sm px-3 py-1 cursor-pointer hover:bg-gray-500 hover:text-white"
                    >
                        Open in PCO
                        <svg xmlns="http://www.w3.org/2000/svg" className="inline-block h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </a>
                </div>

                <h2 className="text-xl font-bold text-gray-800 mb-1">{song.title}</h2>

                {song.author && (
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Authors:</span> {song.author}
                    </p>
                )}

                {song.copyright && (
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Publisher:</span> {song.copyright}
                    </p>
                )}

                {song.ccli_number && (
                    <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">CCLI:</span> {song.ccli_number}
                    </p>
                )}

                {arrangements.length > 1 && (
                    <div className="mt-4">
                        <label className="text-sm font-medium text-gray-700 mr-2">Arrangement</label>
                        <select
                            value={this.state.selectedArrangementIndex}
                            onChange={(e) => this.setState({ selectedArrangementIndex: parseInt(e.target.value), activeTab: 'lyrics' })}
                            className="rounded-sm border-gray-300 text-sm py-1 px-2"
                        >
                            {arrangements.map((arr, i) => (
                                <option key={arr.id} value={i}>{arr.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {arrangement && (
                    <div className="mt-4">
                        <div className="flex border-b border-gray-200 mb-4">
                            <button className={tabClass('lyrics')} onClick={() => this.setState({ activeTab: 'lyrics' })}>
                                Lyrics
                            </button>
                            <button className={tabClass('sections')} onClick={() => this.setState({ activeTab: 'sections' })}>
                                Sections
                            </button>
                        </div>

                        {activeTab === 'lyrics' && (
                            <div>
                                {arrangement.sequence_label && arrangement.sequence_label.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-sm font-medium text-gray-700 mb-1">Sequence</p>
                                        <div className="flex flex-wrap" style={{ gap: 4 }}>
                                            {arrangement.sequence_label.map((label, i) => (
                                                <span key={i} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-sm">
                                                    {label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {arrangement.lyrics ? (
                                    <div className="text-sm text-gray-800 font-mono whitespace-pre-wrap">
                                        {this.formatLyrics(arrangement.lyrics)}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400">No lyrics available for this arrangement.</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'sections' && (
                            <div>
                                {arrangement.sections && arrangement.sections.length > 0 ? (
                                    (() => {
                                        const sectionsByLabel = {};
                                        arrangement.sections.forEach(s => {
                                            sectionsByLabel[s.label.toLowerCase()] = s;
                                        });

                                        const findSection = (label) => {
                                            const key = label.toLowerCase();
                                            if (sectionsByLabel[key]) return sectionsByLabel[key];

                                            // Strip trailing digits/numbers to find base type (e.g., "Verse 3" -> "verse")
                                            const base = key.replace(/\s*\d+$/, '').trim();
                                            // Find first section whose label starts with the same base
                                            const match = arrangement.sections.find(s =>
                                                s.label.toLowerCase().replace(/\s*\d+$/, '').trim() === base
                                            );
                                            return match || null;
                                        };

                                        const sequence = arrangement.sequence_label && arrangement.sequence_label.length > 0
                                            ? arrangement.sequence_label
                                            : arrangement.sections.map(s => s.label);

                                        return sequence.map((label, i) => {
                                            const section = findSection(label);
                                            return (
                                                <div key={i} className="mb-6">
                                                    <h4 className="text-sm font-bold text-gray-400 mb-1 uppercase tracking-wide">
                                                        {label}
                                                    </h4>
                                                    <div className="text-sm text-gray-800 font-mono whitespace-pre-wrap">
                                                        {section && section.lyrics ? this.formatLyrics(section.lyrics) : (
                                                            <p className="text-gray-400 italic">No lyrics for this section.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()
                                ) : (
                                    <p className="text-sm text-gray-400">No sections available for this arrangement.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {!arrangement && (
                    <p className="mt-4 text-sm text-gray-400">No arrangements available for this song.</p>
                )}
            </div>
        );
    }

    render() {
        if (this.props.loading_auth_status) {
            return (
                <div>
                    <SubNav />
                    <LoadingIndicator />
                </div>
            );
        }

        if (!this.props.connected) {
            return this.renderConnectPrompt();
        }

        const songs = this.props.songs || [];

        const hasMore = this.props.songs_next_offset != null;

        let songsSection = (
            <div>
                <div className="px-4 pt-3 pb-1">
                    <div className="relative">
                        <svg className="absolute h-4 w-4 text-gray-400" style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search songs..."
                            value={this.state.searchQuery}
                            onChange={this.handleSearchChange}
                            style={{ paddingLeft: 36 }}
                            className="w-full pr-3 py-2 bg-gray-100 rounded-sm text-sm text-gray-700 placeholder-gray-400 focus:outline-hidden focus:bg-gray-200"
                        />
                    </div>
                </div>
                <Suspense fallback={<LoadingIndicator />}>
                    <SectionHeader text={"Songs" + (this.props.songs_total ? " (" + this.props.songs_total + ")" : "")} />
                    {songs.length === 0 && !this.props.loading_songs && (
                        <div className="px-4 py-3">
                            <div className="bg-gray-100 flex flex-col items-center justify-center rounded-3xl" style={{ aspectRatio: '16/9' }}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 18V5l12-2v13" />
                                    <circle cx="6" cy="18" r="3" />
                                    <circle cx="18" cy="16" r="3" />
                                </svg>
                                <p className="mt-3 text-sm text-gray-400">No songs found</p>
                            </div>
                        </div>
                    )}
                    {songs.map(song => (
                        <TableRow
                            key={song.id}
                            selected={this.props.selected_song && this.props.selected_song.id === song.id}
                            clickEvent={() => this.selectSong(song.id)}
                            text={song.title}
                            detail_text={song.author || ''}
                        />
                    ))}
                    {hasMore && (
                        <div className="py-3 px-4">
                            <button
                                onClick={() => this.props.onFetchSongs(this.props.songs_next_offset, this.state.searchQuery)}
                                disabled={this.props.loading_songs}
                                className="w-full text-sm text-blue-500 hover:text-blue-700 font-medium py-2 cursor-pointer"
                            >
                                {this.props.loading_songs ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}
                </Suspense>
            </div>
        );

        if (this.props.loading_songs && songs.length === 0) {
            songsSection = <LoadingIndicator />;
        }

        return (
            <div className="flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
                <SubNav />
                <div className="flex flex-1 min-h-0 bg-white rounded-sm shadow-md border-b-2">
                    {/* Left pane: song list */}
                    <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                        <ThemeHelpBox hasCustomTheme={false} />
                        {songsSection}
                    </div>
                    {/* Right pane: song detail */}
                    <div className="w-2/3 overflow-y-auto p-6">
                        <Suspense fallback={<LoadingIndicator />}>
                            {this.renderSongDetail()}
                        </Suspense>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loading_auth_status: state.pco_songs.loading_auth_status,
        connected: state.pco_songs.connected,
        connect_url: state.pco_songs.connect_url,
        loading_songs: state.pco_songs.loading_songs,
        songs: state.pco_songs.songs,
        songs_total: state.pco_songs.songs_total,
        songs_next_offset: state.pco_songs.songs_next_offset,
        loading_song_detail: state.pco_songs.loading_song_detail,
        selected_song: state.pco_songs.selected_song,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchAuthStatus: () => dispatch(pcoSongsActions.fetchAuthStatus()),
        onFetchSongs: (offset, query) => dispatch(pcoSongsActions.fetchSongs(offset, query)),
        onFetchSongDetail: (id) => dispatch(pcoSongsActions.fetchSongDetail(id)),
        onExportSong: (songId, arrangementIndex, formatType, songTitle) => dispatch(pcoSongsActions.exportSong(songId, arrangementIndex, formatType, songTitle)),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PcoSongsLayout));
